<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-instant-search
    :index-name="algolia.indexProducts"
    :routing="algoliaRouting"
    :search-client="algolia.searchClient"
    :insights="true"
  >
    <ais-configure
      distinct
      :faceting-after-distinct.camel="true"
      :rule-contexts.camel="['search-modal']"
    />

    <form
      :action="$variable('routes.search')"
      method="get"
      role="search"
    >
      <ais-search-box>
        <template #default="{ refine, currentRefinement }">
          <div class="header-search">
            <icon-search />

            <label
              class="visually-hidden"
              for="menu-drawer-search"
              v-text="$string('predictive_search.placeholder')"
            />

            <input
              id="menu-drawer-search"
              ref="searchRef"
              class="header-search__input text-body-s-regular"
              type="text"
              name="query"
              :placeholder="$string('predictive_search.placeholder')"
              required
              :value="getValue(currentRefinement)"
              @input="updateSearchQuery($event, refine)"
              @click="openSearchModal($event, refine)"
              @keydown="openSearchModal($event, refine)"
            >

            <button
              v-if="searchModalActive"
              class="header-search__clear"
              type="button"
              @click="closeSearchModal(refine)"
            >
              <span
                class="visually-hidden"
                v-text="$string('predictive_search.close')"
              />

              <icon-close />
            </button>
          </div>
        </template>
      </ais-search-box>
    </form>

    <overlay
      key="search-modal"
      block-class="search-modal-overlay"
      direction="down"
      namespace="search-modal"
      :show-close="false"
      type="shelf"
    >
      <template #body="overlay">
        <search-modal
          :overlay="overlay"
          :search-ref="$refs.searchRef"
          :query="input"
        />
      </template>
    </overlay>
  </ais-instant-search>
</template>

<script>

/**
 * Vue: Header search (header-search)
 * -----------------------------------------------------------------------------
 * Header predictive search, Algolia powered.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import { AisConfigure, AisInstantSearch, AisSearchBox } from 'vue-instantsearch/vue3/es'

import Overlay from '~global/overlay/overlay'
import SearchModal from '~async/algolia/search-modal/search-modal'
import IconSearch from '~icons/general/search.svg'
import IconClose from '~icons/directional-navigation/close.svg'

import { debounce } from '~/helpers/general'

const algoliaClient = algoliasearch(
  cnvs.algolia.credentials.appId,
  cnvs.algolia.credentials.apiKey,
)

export default {
  name: 'HeaderSearch',

  components: {
    AisSearchBox,
    AisInstantSearch,
    AisConfigure,
    IconSearch,
    IconClose,
    Overlay,
    SearchModal,
  },

  data() {
    return {
      input: '',
      // If user enters something in input.
      touched: false,
      searchRef: null,
      searchModalActive: false,
      searchFocussed: false,
      algolia: {
        indexProducts: this.$variable('algolia.indices.products'),
        searchClient: {
          ...algoliaClient,
          // Using arrow function to access component scope.
          search: (requests) => {
            // If search is not focused to not send any algolia queries.
            if (!this.searchFocussed) {
              return Promise.resolve({
                results: requests.map(() => ({
                  hits: [],
                  nbHits: 0,
                  nbPages: 0,
                  page: 0,
                  processingTimeMS: 0,
                  hitsPerPage: 0,
                  exhaustiveNbHits: false,
                  query: '',
                  params: '',
                })),
              })
            }

            return algoliaClient.search(requests)
          },
        },
        routing: {
          router: historyRouter(),
          stateMapping: singleIndexMapping(this.$variable('algolia.indices.products')),
        },
      },
      pageType: this.$variable('page.type'),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    algoliaRouting() {
      if (this.pageType !== 'search') {
        return null
      }

      return this.algolia.routing
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay?.namespace === 'search-modal') {
        this.searchModalActive = true
        return
      }

      this.searchModalActive = false
    },
  },

  created() {
    this.debouncedRefineResults = debounce(this.refineResults, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Allows the search query to be updated from the parent.
     * @param {Event} event - Input event.
     * @param {Function} refine - Algolia refine function.
     */
    updateSearchQuery(event, refine) {
      const value = event?.currentTarget?.value || ''
      this.input = value
      this.touched = true

      this.debouncedRefineResults(refine, value)
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Function} refine - Algolia refine function.
     * @param {String} query - Input field value.
     */
    refineResults(refine, query) {
      refine(query)
    },

    getValue(currentRefinement) {

      /**
       * Before interaction with search box, set input from currentRefinement
       * and fire event search-active
       */
      if (!this.touched) {
        this.input = currentRefinement
      }

      return this.input
    },

    /**
     * Handles emitting an event when the input is newly focussed.
     * @param {Object} event - Focus event.
     * @param {Function} refine - Algolia refine function.
     */
    async openSearchModal(event, refine) {
      const { key } = event

      /**
       * Do no open modal on tab and shift key press.
       */
      if (key === 'Tab' || key === 'Shift') {
        return
      }

      this.searchFocussed = true
      refine(this.input || '')

      await this.$nextTick()

      this.openOverlay({
        component: 'search-modal',
        ignoreDismissed: true,
        namespace: 'search-modal',
      })
    },

    /**
     * Clear search input and close search modal.
     * @param {Function} refine - Algolia refine function.
     */
    closeSearchModal(refine) {
      this.input = ''
      this.closeOverlay('search-modal')
      setTimeout(() => refine(''), this.settings.throttle)
    },
  },
}
</script>

<style lang="scss">
@import './header-search';
</style>
